import MoneyInput from "@common/components/form/MoneyInput";
import { TFunction, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useState } from "react";
import { Listbox, SlideToggle } from "@talladega/components";
import { formatCurrency, formatPercent, translate } from "@talladega/helpers";
import { ArrowRightIcon } from "@talladega/icons";
import {
  PaymentFrequencies,
  PaymentFrequency,
  tryGetPaymentFrequencyByName,
} from "@modules/vehicle/types/PaymentFrequency";
import useVehicleFinancePricingBreakdown from "@modules/purchase/consumer/hooks/useVehicleFinancePricingBreakdown";
import { VehiclePricingDefaultResponse } from "@modules/purchase/consumer/types/VehicleFinancePricingBreakdown";
import FinancingPaymentAmountContext from "@modules/purchase/consumer/ConsumerFinancingPageContext";
import useConsumerFinancingFromQuery from "@modules/purchase/consumer/hooks/useConsumerFinancingFromQuery";
import { CmsHeading } from "@talladega/cms";
import { CreditScoreBand } from "@modules/purchase/consumer/types/CreditScoreBand";
import { FinancingCalculatorLayout } from "@modules/cms/types/FinancingCalculatorLayout";
import { LoanTerm } from "@modules/purchase/consumer/types/LoanTerm";
import CmsButton from "@modules/cms/components/CmsButton";
import useGetCreditBands from "@modules/purchase/consumer/hooks/useGetCreditScoreBands";

const getLoanTerms = (t: TFunction): LoanTerm[] => {
  const terms: LoanTerm[] = [];

  for (let i = 1; i <= 8; i += 1) {
    const months = i * 12;
    const name =
      i === 1
        ? t("loan_term_singular", { ns: "purchase" })
        : t("loan_term_plural", {
            ns: "purchase",
            months,
            years: i,
          });

    terms.push({ id: i.toString(), months, name });
  }

  return terms;
};

const getHighestMinScoreBand = (
  creditScoreBands: CreditScoreBand[]
): CreditScoreBand | undefined => {
  if (!creditScoreBands?.length) {
    return undefined;
  }
  return creditScoreBands.reduce(
    (max, band) => (band.minScore > max.minScore ? band : max),
    creditScoreBands[0]
  );
};

type Props = {
  pricingDefaults: VehiclePricingDefaultResponse;
  onStartFinancing: () => void;
  cmsProps: FinancingCalculatorLayout;
};

const FinancingCalculator = ({
  pricingDefaults,
  onStartFinancing,
  cmsProps,
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const [loanTerms] = useState<LoanTerm[]>(getLoanTerms(t));
  const { financingState } = useContext(FinancingPaymentAmountContext);
  const [, setFinancingState] = financingState ?? [{}];

  const { heading, subheading, button } = cmsProps;

  const { purchasePrice, year } = useConsumerFinancingFromQuery();
  const [purchaseAmount, setPurchaseAmount] = useState<number>(
    purchasePrice || 0
  );

  const [selectedPaymentFrequency, setSelectedPaymentFrequency] =
    useState<PaymentFrequency>(
      () =>
        Object.values(PaymentFrequencies).find(
          (pf) => pf.id === pricingDefaults.paymentFrequencyId
        ) || PaymentFrequencies.biWeekly
    );

  const [selectedLoanTerm, setSelectedLoanTerm] = useState<LoanTerm>(
    () =>
      loanTerms.find(
        (lt) => lt.months === (pricingDefaults.maxTermInMonths as number)
      ) ??
      loanTerms.find((lt) => lt.months === pricingDefaults.termInMonths) ??
      loanTerms[0]
  );

  const { bands } = useGetCreditBands();
  const updatedCreditBands: CreditScoreBand[] = useMemo(
    () =>
      bands.map((band, index) => ({
        ...band,
        name: `${band.minScore}${
          bands[index + 1]?.minScore
            ? ` - ${bands[index + 1].minScore - 1}`
            : "+"
        }`,
      })),
    [bands]
  );

  const [selectedCreditBand, setSelectedCreditBand] =
    useState<CreditScoreBand | null>(null);

  useEffect(() => {
    // Sets the default credit band on page load.
    // This prevents overwriting user's selection if the bands data refetches.
    if (updatedCreditBands.length > 0 && !selectedCreditBand) {
      const highestBand = getHighestMinScoreBand(updatedCreditBands);
      if (highestBand) {
        setSelectedCreditBand(highestBand);
      }
    }
  }, [updatedCreditBands, selectedCreditBand]);

  useEffect(() => {
    if (!router.isReady) return;
    // Sets the default vehicle price based on given query params.
    if (purchaseAmount === 0 && purchasePrice && purchasePrice > 0) {
      setPurchaseAmount(purchasePrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasePrice, router.isReady]);

  const { pricingBreakdown } = useVehicleFinancePricingBreakdown({
    sellingPrice: purchaseAmount,
    cashDownAmount: 0,
    paymentFrequencyId: selectedPaymentFrequency.id,
    termInMonths: selectedLoanTerm.months,
    buyerProvince: "", // Don't calculate taxes for C2C.
    vehicleYear: year || 0,
    rate: selectedCreditBand?.rate ?? pricingDefaults.interestRate,
  });

  useEffect(() => {
    if (setFinancingState) {
      setFinancingState({
        paymentAmount: pricingBreakdown?.paymentAmount || 0,
        paymentFrequency: selectedPaymentFrequency,
      });
    }
  }, [
    selectedPaymentFrequency,
    pricingBreakdown?.paymentAmount,
    setFinancingState,
  ]);

  return (
    <div className="w-full">
      <div className="bg-secondary rounded-2xl p-4 md:py-6 lg:px-10 lg:pt-8 lg:pb-6 mt-6 md:mt-5 shadow-md">
        <div className="flex flex-col items-start gap-2 mb-8">
          {subheading && (
            <CmsHeading
              headingLevel={subheading.headingGroup?.headingLevel}
              textSize={subheading.headingGroup?.textSize}
              text={subheading.headingGroup?.text}
              htmlId={subheading.headingGroup?.htmlId}
            />
          )}
          {heading && (
            <CmsHeading
              headingLevel={heading.headingGroup?.headingLevel}
              textSize={heading.headingGroup?.textSize}
              text={heading.headingGroup?.text}
              htmlId={heading.headingGroup?.htmlId}
            />
          )}
        </div>

        <div
          id="calculator-summary-container"
          className="flex gap-y-6 md:gap-y-0 gap-x-6 md:gap-x-0 flex-col md:flex-row"
        >
          <div
            id="calculator-section"
            className="basis-[45%] flex flex-col gap-6 md:mr-16 lg:mr-24"
          >
            <div className="h-[2.6rem]">
              <SlideToggle
                leftLabel={translate(PaymentFrequencies.monthly.name, locale)}
                leftValue={translate(PaymentFrequencies.monthly.name, locale)}
                rightLabel={translate(PaymentFrequencies.biWeekly.name, locale)}
                rightValue={translate(PaymentFrequencies.biWeekly.name, locale)}
                value={translate(selectedPaymentFrequency.name, locale)}
                onChange={(selected) => {
                  setSelectedPaymentFrequency(
                    tryGetPaymentFrequencyByName(selected) ||
                      PaymentFrequencies.monthly
                  );
                }}
                rounded="lg"
              />
            </div>
            <MoneyInput
              id="vehiclePrice"
              label={t("purchase:vehicle_price")}
              locale={locale || ""}
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e)}
            />
            <Listbox
              items={loanTerms}
              label={t("common:term")}
              keyOption={(item) => item.id}
              displayOption={(item) => item?.name ?? ""}
              displayButton={(item) => item?.name ?? ""}
              valueOption={(item) => item}
              selectedItem={selectedLoanTerm}
              onChange={(item) => {
                setSelectedLoanTerm(item);
              }}
            />
            <Listbox
              items={updatedCreditBands}
              label={t("purchase:credit_score")}
              keyOption={(item) => item?.id ?? ""}
              displayOption={(item) => item?.name ?? ""}
              displayButton={(item) => item?.name ?? ""}
              valueOption={(item) => item}
              selectedItem={selectedCreditBand}
              onChange={(item) => {
                setSelectedCreditBand(item);
              }}
            />
            <p className="hidden md:block text-xs text-secondary-700">
              {t("purchase:c2c_calculator_disclaimer")}
            </p>
          </div>
          <div
            id="summary-section"
            className="basis-[55%] bg-white rounded-xl shadow-md py-6 px-4 md:px-8 h-fit"
          >
            <div className="flex flex-col gap-4">
              <div className="flex gap-2">
                <div className="flex flex-col mr-auto items-start gap-1">
                  <p className="text-tertiary text-sm">{t("common:terms")}</p>
                  <p className="font-bold text-xl">{`${
                    selectedLoanTerm.months
                  } ${t("common:months")}`}</p>
                  <p className="text-quaternary font-medium">
                    {`${formatPercent(
                      pricingBreakdown?.interestRate ??
                        pricingDefaults.interestRate,
                      locale
                    )} ${t("purchase:apr")}`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 whitespace-nowrap">
                  <p className="text-tertiary text-sm">{t("common:payment")}</p>
                  <p className="font-bold text-xl">
                    {`${formatCurrency(
                      pricingBreakdown?.paymentAmount || 0,
                      locale
                    )}/${translate(
                      selectedPaymentFrequency.name,
                      locale
                    ).substring(0, 2)}*`}
                  </p>
                  <p className="text-quaternary font-medium text-base">
                    {`${t("purchase:total_amount")}: ${formatCurrency(
                      pricingBreakdown?.totalFinancedPrice || 0,
                      locale
                    )}`}
                  </p>
                </div>
              </div>
              <div className="hidden md:block border-t border-gray-200" />
              <div className="hidden md:flex flex-col items-start gap-4 bg-secondary py-5 px-6 rounded-xl">
                <p className="text-lg font-bold">
                  {t("purchase:fast_and_easy_financing")}
                </p>
                <p className="text-tertiary">
                  {t("purchase:we_will_help_you_every_step")}
                </p>
                <CmsButton
                  className="w-full"
                  button={button}
                  rightIcon={<ArrowRightIcon />}
                  setButtonModalState={onStartFinancing}
                />
              </div>
            </div>
          </div>
          <CmsButton
            className="md:hidden w-full"
            button={button}
            rightIcon={<ArrowRightIcon />}
            setButtonModalState={onStartFinancing}
          />
          <div className="md:hidden text-xs text-secondary-700">
            {t("purchase:c2c_calculator_disclaimer")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingCalculator;
